/**
 * Availability Outlook Service
 *
 * Data access for the admin "Availability Outlook" heatmap: free rental stock
 * per product per day for a single location over a bounded window.
 *
 * Backed by the additive, read-only `get_availability_outlook` RPC (see
 * migration 20261102000000). The RPC is SECURITY DEFINER and enforces its own
 * merchant/location authorization, so scope is passed as the explicit
 * `p_location_id` param rather than via `applyAccessControl` (which only
 * decorates query-builders, not RPC calls). The page restricts the location
 * dropdown to accessible locations, and the RPC re-checks server-side.
 *
 * Drill-down (a single day+product) does NOT use this service — it calls the
 * canonical `checkAvailability` in bookings.service so inspected numbers are
 * authoritative.
 *
 * Wire shape: the RPC returns ONE ROW PER (product_type × size) bucket with the
 * per-day numbers as parallel arrays ordered by day (`days`, `capacity`,
 * `booked`, `blocked`, `free`, `is_closed`). The row count therefore depends
 * only on the catalogue, not on the window length, so a whole 12-week window
 * is a single request and the PostgREST `max-rows` cap (1000) is only
 * reachable with 1000+ size buckets at one location. This service expands the
 * arrays into the flat per-cell `OutlookRow[]` the page consumes, so the page
 * is unaware of the wire shape.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';

export type OutlookInventoryType = 'individual' | 'pool' | 'unlimited';

/** One (product_type × size × day) cell, as the page consumes it. */
export interface OutlookRow {
  day: string;                       // 'YYYY-MM-DD' (merchant tz)
  locationId: string;
  productTypeId: string;
  productName: string;
  categoryId: string | null;
  inventoryType: OutlookInventoryType;
  productType: string | null;        // 'bike' | 'addon'
  size: string | null;               // variant; null = sizeless / pool / unlimited
  capacity: number | null;           // null = unlimited; RAW per size for individual
  booked: number;
  blocked: number;
  free: number | null;               // null = unlimited
  maxConcurrent: number | null;      // product-level concurrency cap (null = none)
  isClosed: boolean;
}

export interface GetOutlookParams {
  locationId: string;
  /** Inclusive window in merchant-local calendar dates, 'YYYY-MM-DD'. */
  startYmd: string;
  endYmd: string;
  categoryId?: string;
  productTypeId?: string;
}

/** One bucket row exactly as the RPC returns it (snake_case, parallel arrays). */
interface OutlookBucketRow {
  location_id: string;
  product_type_id: string;
  product_name: string;
  category_id: string | null;
  inventory_type: OutlookInventoryType;
  product_type: string | null;
  size: string | null;
  max_concurrent: number | null;
  days: string[];
  capacity: (number | null)[];
  booked: (number | null)[];
  blocked: (number | null)[];
  free: (number | null)[];
  is_closed: (boolean | null)[];
}

/**
 * The PostgREST `max-rows` setting (supabase/config.toml). A response holding
 * exactly this many bucket rows is indistinguishable from one that was cut
 * off, so it is treated as truncated and refused rather than rendered with
 * products silently missing.
 */
const MAX_ROWS = 1000;

function toInt(v: unknown): number {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function toNullableInt(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Expand one bucket row into its per-day cells. */
function expandBucket(r: OutlookBucketRow): OutlookRow[] {
  const days = r.days || [];
  const out: OutlookRow[] = new Array(days.length);
  for (let i = 0; i < days.length; i++) {
    out[i] = {
      day: days[i],
      locationId: r.location_id,
      productTypeId: r.product_type_id,
      productName: r.product_name,
      categoryId: r.category_id ?? null,
      inventoryType: r.inventory_type,
      productType: r.product_type ?? null,
      size: r.size ?? null,
      capacity: toNullableInt(r.capacity?.[i]),
      booked: toInt(r.booked?.[i]),
      blocked: toInt(r.blocked?.[i]),
      free: toNullableInt(r.free?.[i]),
      maxConcurrent: toNullableInt(r.max_concurrent),
      isClosed: !!r.is_closed?.[i],
    };
  }
  return out;
}

/**
 * Fetch the availability outlook grid for one location and date window in a
 * single request. Returns a flat list of (product × size × day) rows; the page
 * shapes it into a matrix and sorts for display, so row order carries no
 * meaning.
 */
export async function getAvailabilityOutlook(
  params: GetOutlookParams
): Promise<{ data: OutlookRow[] | null; error: string | null }> {
  try {
    if (!params.locationId) {
      return { data: [], error: null };
    }

    // `get_availability_outlook` is a custom RPC not in the generated Database
    // types (same as other custom RPCs, e.g. get_customers), so the call is
    // cast to bypass the typed rpc-name union.
    const { data, error } = await (supabase.rpc as any)('get_availability_outlook', {
      p_location_id: params.locationId,
      p_start_date: params.startYmd,
      p_end_date: params.endYmd,
      p_category_id: params.categoryId || null,
      p_product_type_id: params.productTypeId || null,
    });

    if (error) throw error;

    const buckets = (data as OutlookBucketRow[]) || [];
    if (buckets.length >= MAX_ROWS) {
      throw new Error(
        `The availability outlook for this location has ${MAX_ROWS} or more ` +
        'product/size rows, which exceeds the response limit. Filter by ' +
        'category or product to narrow it.'
      );
    }

    const rows: OutlookRow[] = [];
    for (const b of buckets) {
      rows.push(...expandBucket(b));
    }

    return { data: rows, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/** State a cell renders as, derived from the row's numbers. */
export type OutlookState = 'available' | 'tight' | 'full' | 'maintenance' | 'closed' | 'unlimited';

/**
 * Derive the display state for a cell. Kept here so the page and any future
 * consumer classify cells identically.
 *
 * Priority: closed > unlimited > (whole product-day blocked) maintenance >
 * full (0 free) > tight (<= a third free) > available.
 */
export function outlookCellState(row: OutlookRow): OutlookState {
  if (row.isClosed) return 'closed';
  if (row.inventoryType === 'unlimited' || row.capacity === null || row.free === null) return 'unlimited';
  if (row.capacity > 0 && row.blocked >= row.capacity && row.booked === 0) return 'maintenance';
  if (row.free <= 0) return 'full';
  if (row.free <= Math.ceil(row.capacity / 3)) return 'tight';
  return 'available';
}
